
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Mind, TacMind } from "@/lib/model/exambuilder/exam-component/company";
import {
  ExamComponent,
  ExamVersionInfo,
  SectionPageType
} from "@/lib/model/exambuilder/exam-component/exam";
import { TacPsycConfig } from "@/lib/model/exambuilder/exam-component/tac-psyc";
import InputNumberV3 from "@/views/ai-builder-v3/components/inputNumberV3.vue";
import Pop from "../components/blue-pop.vue";
import IntroduceCard from "../components/introduce-card.vue";
import HinaSwitch from "../baseComponent/hina-switch.vue";
import HinaSelect from "../baseComponent/hina-select.vue";
import HinaInput from "../baseComponent/hina-input.vue";

class MindGroup {
  name = "";
  desc = "";
  mindIdList: number[] = [];
  mindList: TacMind[] = [];
}

@Component({
  components: {
    HinaInput,
    HinaSelect,
    HinaSwitch,
    Pop,
    InputNumberV3,
    IntroduceCard
  }
})
export default class TacPsycGauge extends Vue {
  @Prop() formItemData!: ExamVersionInfo; // 组件数据
  @Prop({ default: "" }) builder!: string; // 创建面试来源

  created() {
    this.getMindList();
  }

  trialOne() {
    this.$emit("trial", "tacPsycGauge");
  }

  // 已有配置
  get psycConfigList() {
    return this.formItemData.tacPsycGauge.configDataList;
  }

  @Watch("psycConfigList", { deep: true, immediate: true })
  handleChangePsycList() {
    if (this.psycConfigList.length) {
      this.formItemData.tacPsycGauge.use = 1;
    } else {
      this.formItemData.tacPsycGauge.use = 2;
    }
    this.$emit("setGlobal");
  }

  tacMindGroup: MindGroup[] = [
    {
      name: "大五人格心理测评",
      desc:
        "大五人格是心理学中基于人格特质论的一种人格分类方法，从上世纪80年代开始得到广泛发展和应用。",
      mindIdList: [4, 5],
      mindList: []
    },
    {
      name: "DISC性格测评",
      desc:
        "DISC性格测试，是根据美国心理学家威廉马斯顿教授的个人激励理论而创建的行为因素分析测试。目前，DISC理论已被广泛应用于世界500强企业的人才招聘、培养、管理、激励领域，历史悠久、专业性强、权威性高。",
      mindIdList: [2],
      mindList: []
    },
    {
      name: "DVF心理健康测评",
      desc:
        "DVF心理健康测评是一种用于评估个体心理健康状况的工具，包含33个题目。该量表经过严格的信效度检验，具有很好的区分度和稳定性，可以为候选人心理健康评估提供重要参考。",
      mindIdList: [1],
      mindList: []
    }
  ];
  tacMindList: TacMind[] = [];
  mindConfigMap: { [key: number]: TacPsycConfig } = {};

  async getMindList() {
    try {
      const res = await Mind.get();
      this.tacMindList = res.tacList.map((item: TacMind) => {
        return new TacMind().assign(item);
      });
      this.initTacMindGroup();
      this.initTacMindConfig();
    } catch (err) {
      console.error(err);
    }
  }

  // 初始化组
  initTacMindGroup() {
    this.tacMindGroup.forEach(item => {
      item.mindList = this.tacMindList.filter(mItem =>
        item.mindIdList.includes(mItem.id)
      );
    });
  }

  // 初始化配置
  initTacMindConfig() {
    this.tacMindList.forEach(item => {
      const config = this.psycConfigList.find(cItem => cItem.id === item.id);

      if (!config) {
        return this.resetTacMindConfig(item);
      }

      this.mindConfigMap[item.id] = new TacPsycConfig().assign({
        ...config,
        segScore: config.dimensionParams[0]?.segScore || [95, 90]
      });
    });
  }

  // 重制配置
  resetTacMindConfig(mind: TacMind) {
    this.mindConfigMap[mind.id] = new TacPsycConfig().assign({
      id: mind.id,
      sectionPageConfig: ExamComponent.getDefaultSectionPage(
        SectionPageType.Psyc,
        mind
      ),
      dimensionParams: mind.dimensionList.map(d => ({
        id: d.id,
        segScore: [95, 90]
      }))
    });
  }

  // 选中状态检测
  checkSelect(mind: TacMind) {
    return this.psycConfigList.some(item => item.id === mind.id);
  }

  getSelectQuestionCount(mind: TacMind) {
    const config = this.mindConfigMap[mind.id];

    if (!config) return 0;

    if (!mind.dimensionList.length) return mind.questionCount;

    return (
      (mind.questionCount / mind.dimensionList.length) *
      config.selectDimensionIds.length
    );
  }

  getSelectDimensions(mind: TacMind) {
    const config = this.mindConfigMap[mind.id];

    if (!config) return [];

    if (!mind.dimensionList.length) return [];

    return mind.dimensionList.filter(item =>
      config.selectDimensionIds.includes(item.id)
    );
  }

  // 弹窗
  activeTacMind = new TacMind();
  activeConfig = new TacPsycConfig();

  // 查看详情
  showDetail = false;

  handleShowDetail(mind: TacMind) {
    this.activeTacMind.assign(mind);
    this.activeConfig.assign(this.mindConfigMap[mind.id]);
    this.showDetail = true;
  }

  // 选择量表
  handleSelect(mind: TacMind) {
    const i = this.psycConfigList.findIndex(item => item.id === mind.id);

    if (i === -1) {
      this.activeTacMind.assign(mind);
      this.activeConfig.assign(this.mindConfigMap[mind.id]);

      // 选择子维度
      if (mind.canDimensionSelection === 1) {
        return (this.showDimensions = true);
      }

      // dvf 2.0特判
      if (mind.id === 1) {
        return (this.showDvf2Config = true);
      }

      return this.saveConfig();
    } else {
      this.psycConfigList.splice(i, 1);
      this.resetTacMindConfig(mind);
    }
  }

  openSelect(mind: TacMind) {
    this.activeTacMind.assign(mind);
    this.activeConfig.assign(this.mindConfigMap[mind.id]);
    this.showDimensions = true;
  }

  saveConfig() {
    // 同分组只能选择一个
    const group = this.tacMindGroup.find(item =>
      item.mindIdList.includes(this.activeConfig.id)
    );

    if (!group || group.mindList.length === 1) {
      this.mindConfigMap[this.activeConfig.id] = this.activeConfig;
      this.psycConfigList.push(this.activeConfig);
      this.activeTacMind = new TacMind();
      this.activeConfig = new TacPsycConfig();
      this.$forceUpdate();
      return;
    }

    // 将已选中的先删除
    group.mindList.forEach(mItem => {
      const i = this.psycConfigList.findIndex(sItem => sItem.id === mItem.id);

      if (i !== -1) {
        this.psycConfigList.splice(i, 1);
        this.resetTacMindConfig(mItem);
      }
    });

    this.mindConfigMap[this.activeConfig.id] = this.activeConfig;
    this.psycConfigList.push(this.activeConfig);
    this.activeTacMind = new TacMind();
    this.activeConfig = new TacPsycConfig();
    this.$forceUpdate();
  }

  // 选择子维度
  showDimensions = false;

  selectDimension(id: number) {
    const i = this.activeConfig.selectDimensionIds.findIndex(res => res === id);

    if (i === -1) {
      this.activeConfig.selectDimensionIds.push(id);
    } else {
      this.activeConfig.selectDimensionIds.splice(i, 1);
    }

    this.$forceUpdate();
  }

  clearDimension() {
    this.activeConfig.selectDimensionIds = [];
    this.$forceUpdate();
  }

  checkDimension(id: number) {
    return !!this.activeConfig?.selectDimensionIds.includes(id);
  }

  cancelDimension() {
    this.showDimensions = false;
    this.activeTacMind = new TacMind();
    this.activeConfig = new TacPsycConfig();
  }

  handleOkDimension() {
    if (!this.activeConfig.selectDimensionIds.length) {
      return this.$message.error("请至少选择一个维度");
    }

    const i = this.psycConfigList.findIndex(
      item => item.id === this.activeConfig.id
    );

    if (i === -1) {
      this.saveConfig();
    } else {
      this.mindConfigMap[this.activeConfig.id] = this.activeConfig;
      this.$set(this.psycConfigList, i, this.activeConfig);
      this.activeTacMind = new TacMind();
      this.activeConfig = new TacPsycConfig();
      this.$forceUpdate();
    }

    this.showDimensions = false;
  }

  // 选择高级配置 - dvf2.0特判
  showDvf2Config = false;

  handleOkDvf2Config() {
    this.saveConfig();
    this.showDvf2Config = false;
  }

  cancelDvf2Config() {
    this.showDvf2Config = false;
    setTimeout(() => {
      this.activeTacMind = new TacMind();
      this.activeConfig = new TacPsycConfig();
    }, 300);
  }

  // 校验数据
  formItemCheck() {
    return new Promise((resolve, reject) => {
      const valid = true;
      if (valid) {
        resolve("");
      } else {
        resolve("AiAnswer");
      }
    });
  }
}
